import {
  AuditorFirmTeamsResponse,
  InviteTeamMemberPayload,
  UpdateTeamPayload,
  TeamResponse,
  UpdateTeamMemberPayload,
  UpdateTeamMemberSignatoryPayload,
  UpdateTeamMemberSignatoryResponse,
} from '@/api';
import API from '@api/API';
import { ENDPOINTS } from '../endpoints';

export const teamApi = {
  getTeam: async (): Promise<TeamResponse> => {
    const response = await API().get(ENDPOINTS.team.team());
    return response.data;
  },

  listAuditorFirms: async (): Promise<AuditorFirmTeamsResponse> => {
    const response = await API().get(ENDPOINTS.team.auditorFirms());
    return response.data;
  },

  /**
   * `POST /team/update` is multipart and reads exactly two fields: `name` and `logo`
   * (`team.controller.js` → `logo: logo === "undefined" ? null : uploadedFile?.key`).
   *
   * Deleting the logo is signalled by the literal STRING `'undefined'`. That is not a
   * bug being worked around — it is the tested backend contract (`team-error-handling`
   * covers `logo === "undefined"` → `null`) and the same sentinel `updateUser` already
   * sends for the user avatar.
   *
   * The trap: any OTHER value with no file attached resolves to `undefined`, and Prisma
   * treats `undefined` as "leave this column alone". So a removal that omits `logo` —
   * which is what `postForm` did, because axios drops `null` — silently no-ops and the
   * API answers 200 with the old logo still attached.
   *
   * Hence the three explicit cases below: File → upload, cleared → sentinel, unchanged →
   * send nothing and let Prisma skip the column. `logoURL` is a local object URL used
   * only for preview; the backend has no such field and must never receive it.
   */
  updateTeam: async (data: UpdateTeamPayload): Promise<TeamResponse> => {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (key === 'logoURL') return;

      if (key === 'logo') {
        if (value instanceof File) {
          formData.append('logo', value);
        } else if (!value) {
          formData.append('logo', 'undefined');
        }
        return;
      }

      if (typeof value === 'boolean') {
        formData.append(key, String(value));
      } else if (value != null && value !== '') {
        formData.append(key, String(value));
      }
    });

    const response = await API().post(ENDPOINTS.team.update(), formData);
    return response.data;
  },

  inviteUser: async (data: InviteTeamMemberPayload): Promise<TeamResponse> => {
    const response = await API().post(ENDPOINTS.team.invite(), data);
    return response.data;
  },

  changeTeamSignatory: async (
    id: string,
    data: UpdateTeamMemberSignatoryPayload,
  ): Promise<UpdateTeamMemberSignatoryResponse> => {
    const response = await API().post(ENDPOINTS.team.signatory(id), data);
    return response.data;
  },

  updateTeamMember: async (id: string, data: UpdateTeamMemberPayload): Promise<TeamResponse> => {
    const response = await API().post(ENDPOINTS.team.member(id), data);
    return response.data;
  },

  removeTeamMember: async (id: string): Promise<TeamResponse> => {
    const response = await API().delete(ENDPOINTS.team.member(id));
    return response.data;
  },
};
